import classNames from 'classnames';
import {observer} from 'mobx-react';
import React from 'react';
import ReactInputMask from 'react-input-mask';
import {User} from '../../../../domain';
import {
  ClassroomStatusSelect,
  TooltipDiv,
  TooltipSpan,
  X4UnitSelect,
  X5LessonSelect,
  InputAutoWidth,
  IntegerInput
} from '../../../../lib/components';
import {concat_ws, currentTimeZone, ReactNodeGenerator} from '../../../../lib/helpers';
import {routes} from '../../../../pages/Routes';
import {ClassroomEditFormStore} from './ClassroomEditFormStore';
const font = 'normal 400 14px Roboto';

const datePlaceholder = 'yyyy-mm-dd';
const timePlaceholder = 'hh:mm';

function userTooltipContent(user: User): React.ReactNode {
  return (
    <div>
      <div>Username: {user.Username}</div>
      <div>First Name: {user.FirstName}</div>
      <div>Last Name: {user.LastName}</div>
      <div>Email: {user.Email}</div>
    </div>);
}

function renderStudent(store: ClassroomEditFormStore): React.ReactNode {
  const student = store.student.value;

  if (student === null)
    return (
      <div className="alert alert-info">
        No student selected
      </div>
    );

  return (
    <div className="form-group">
      <label>Student</label>
      <TooltipDiv className="text-box" tooltipText={userTooltipContent(student)}>
        {concat_ws([student.FirstName, student.LastName])}
      </TooltipDiv>
    </div>
  );
}

function renderTeacher(store: ClassroomEditFormStore): React.ReactNode {
  const teacher = store.teacher.value;

  if (teacher === null)
    return (
      <div className="alert alert-info">
        No teacher selected
      </div>
    );

  return (
    <div className="form-group">
      <label>Teacher</label>
      <TooltipDiv className="text-box" tooltipText={userTooltipContent(teacher)}>
        {concat_ws([teacher.FirstName, teacher.LastName])}
      </TooltipDiv>
    </div>
  );

}

function renderDateTimeControls(store: ClassroomEditFormStore): React.ReactNode {
  const student = store.student.value;
  const teacher = store.teacher.value;

  const {studentStartDate, studentStartTime} = store;
  const {teacherStartDate, teacherStartTime} = store;
  const {adminStartDate, adminStartTime} = store;

  const adminTimeTooltip = 'Admin:' + currentTimeZone().UtcName;
  const studentTimeTooltip = student === null
    ? 'Student Time'
    : 'Student Time:' + student.TimeZone!.UtcName;
  const teacherTimeTooltip = teacher === null
    ? 'Teacher Time'
    : 'Teacher Time:' + teacher.TimeZone!.UtcName;

  const dateControlClassName = classNames('form-group', 'tz-date',
    {
      'has-error': adminStartDate.touched && store.startTime.value === null
    });
  const timeControlClassName = classNames('form-group', 'tz-time', {
    'has-error': adminStartTime.touched && store.startTime.value === null
  });

  return [
    <div className="row" key="labels">
      <div className="col-auto">
        <div className="form-group tz-date">
          <label>Start date</label>
        </div>
      </div>
      <div className="col-auto">
        <div className="form-group tz-time">
          <label>Start time</label>
        </div>
      </div>
      <div className="col-auto">
        <div className="tz-tooltip">
          <span className="fas fa-question-circle" style={{visibility: 'hidden'}}/>
        </div>
      </div>
    </div>,
    <div className="row align-items-center" key="admin-tz">
      <div className="col-auto">
        <div className={dateControlClassName}>
          <ReactInputMask className="form-control date admin"
                          mask="9999-99-99"
                          value={adminStartDate.value}
                          onChange={store.adminStartDateChangeHandler}
                          onBlur={adminStartDate.markTouched}
                          placeholder={datePlaceholder}/>
        </div>
      </div>
      <div className="col-auto">
        <div className={timeControlClassName}>
          <ReactInputMask className="form-control time admin"
                          mask="99:99"
                          value={adminStartTime.value}
                          onChange={store.adminStartTimeChangeHandler}
                          onBlur={adminStartTime.markTouched}
                          placeholder={timePlaceholder}/>
        </div>
      </div>
      <div className="col-auto">
        <div className="tz-tooltip">
          <TooltipSpan className="fas fa-question-circle"
                       tooltipText={adminTimeTooltip}/>
        </div>
      </div>
      
    </div>,
    <div className="row align-items-center" key="student-tz">
      <div className="col-auto">
        <div className={dateControlClassName}>
          <ReactInputMask className="form-control date student"
                          mask="9999-99-99"
                          value={studentStartDate.value}
                          onChange={store.studentStartDateChangeHandler}
                          onBlur={adminStartDate.markTouched}
                          disabled={student === null}
                          placeholder={datePlaceholder}/>
        </div>
      </div>
      <div className="col-auto">
        <div className={timeControlClassName}>
          <ReactInputMask className="form-control time student"
                          mask="99:99"
                          value={studentStartTime.value}
                          onChange={store.studentStartTimeChangeHandler}
                          onBlur={adminStartTime.markTouched}
                          disabled={student === null}
                          placeholder={timePlaceholder}/>
        </div>
      </div>
      <div className="col-auto">
        <div className="tz-tooltip">
          <TooltipSpan className="fas fa-question-circle"
                       tooltipText={studentTimeTooltip}/>
        </div>
      </div>
    </div>,
    <div className="row align-items-center" key="teacher-tz">
      <div className="col-auto">
        <div className={dateControlClassName}>
          <ReactInputMask className="form-control date teacher"
                          mask="9999-99-99"
                          value={teacherStartDate.value}
                          onChange={store.teacherStartDateChangeHandler}
                          onBlur={adminStartDate.markTouched}
                          disabled={teacher === null}
                          placeholder={datePlaceholder}/>
        </div>
      </div>
      <div className="col-auto">
        <div className={timeControlClassName}>
          <ReactInputMask className="form-control time teacher"
                          mask="99:99"
                          value={teacherStartTime.value}
                          onChange={store.teacherStartTimeChangeHandler}
                          onBlur={adminStartTime.markTouched}
                          disabled={teacher === null}
                          placeholder={timePlaceholder}/>
        </div>
      </div>
      <div className="col-auto">
        <div className="tz-tooltip">
          <TooltipSpan className="fas fa-question-circle"
                       tooltipText={teacherTimeTooltip}/>
        </div>
      </div>
    </div>
  ];
}

function renderWeekControls(store: ClassroomEditFormStore) {
  const invalidDate = store.startTime.value === null;

  return (<>
    <button className="btn btn-sm btn-outline-primary"
            disabled={invalidDate}
            onClick={store.previousWeek}>
      <i className="fas fa-angle-double-up"/>
    </button>
    <br/>
    <button className="btn btn-sm btn-outline-primary"
            disabled={invalidDate}
            onClick={store.nextWeek}>
      <i className="fas fa-angle-double-down"/>
    </button>
  </>);
}

function* renderLessonControls(store: ClassroomEditFormStore): ReactNodeGenerator {
  const student = store.student.value;

  if (student !== null && student.Student !== null && student.Student.LevelId === null) {
    yield (
      <div className="col-md-8 col-xs-6" key="no-student-level">
        <div className="row">
          <div className="col">
            <div className="alert alert-info">
              No level specified for student.
            </div>
          </div>
        </div>
        <div className="col-auto">
          <a className="btn btn-primary"
             target="_blank" rel="noopener noreferrer"
             href={routes.userView(student!.Id)}>
            View
          </a>
        </div>
      </div>);
    return;
  }

  if (student === null || student.Student === null || student.Student.LevelId === null)
    return;

  const {unitOptions, unit} = store;
  const {lessonOptions, lesson} = store;

  yield (
    <div key="select-unit">
      <div className={unit.formGroupClassName}>
        <label>Unit</label>
        <X4UnitSelect options={unitOptions}
                      value={unit.value}
                      onChange={unit.setValue}
                      onBlur={unit.markTouched}/>
        <div className="invalid-text"> Select unit</div>
      </div>
    </div>
  );

  yield (
    <div key="select-lesson">
      <div className={lesson.formGroupClassName}>
        <label>Lesson</label>
        <X5LessonSelect options={lessonOptions}
                        value={lesson.value}
                        onChange={lesson.setValue}
                        onBlur={lesson.markTouched}/>
        <div className="invalid-text"> Select lesson</div>
      </div>
    </div>
  );

  const {status} = store;
  yield (
    <div key="select-classroom-status">
      <div className={status.formGroupClassName}>
        <label>Status</label>
        <ClassroomStatusSelect value={status.value}
                               onChange={status.setValue}
                               onBlur={status.markTouched}/>
        <div className="invalid-text"> Select classroom status</div>
      </div>
    </div>
  );
}

@observer
export class ClassroomEditForm extends React.Component<{
  store: ClassroomEditFormStore,
  title: string
}> {
  render() {
    const store = this.props.store;

    return (
      <div className="row">
        <div className="col">
          <div className="card admin-classroom-scene classroom-edit-form">
            <div className="card-header">
              {this.props.title}
            </div>
            <div className="card-body">
              <div className="row justify-content-between">
                <div className="col-auto">
                  {renderStudent(store)}
                  {renderTeacher(store)}
                </div>
                <div className="col-auto">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      {renderDateTimeControls(store)}
                    </div>
                    <div className="col-auto">
                      {renderWeekControls(store)}
                    </div>
                    <div className="col-auto">
                      <div className="form-group user-search-string">
                          <label>Pending time(in minutes)</label>
                          <ReactInputMask className="form-control time student"
                            mask="999"
                            value={store.PendingTime.value}
                            onChange={store.pendingTimeChangeHandler}
                            onBlur={store.PendingTime.markTouched}
                            placeholder="Pending time"/>
                          
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 col-xs-6">
                  {Array.from(renderLessonControls(this.props.store))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
